import React, { useEffect, useMemo } from "react";
import { Drawer, FormInput, FormSelect, Button } from 'components/common';
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { FiAlertCircle } from 'react-icons/fi';
import TinymceEditor from "components/common/TinymceEditor";
import { createFaq, getCategory, updateFaq } from "redux/documentation/faqSlice";
import PropTypes from 'prop-types';
import { toast } from "react-toastify";

const FaqForm = ({ showModal, setshowModal, title, faq, onSaved }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue } = useForm({
    defaultValues: {
      title: '',
      category_id: '',
      description: ''
    }
  });
  const { categoryData } = useSelector(state => state.documentation_faq);
  const dispatch = useDispatch();

  useEffect(() => {
    if (faq) {
      Object.entries(faq).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [faq, setValue]);

  useEffect(() => {
    if (categoryData && categoryData.length === 0) {
      dispatch(getCategory());
    }
  }, [dispatch, categoryData]);

  const closeModal = () => {
    setshowModal(false);
  };

  const onSubmit = async (data) => {
    try {
      if (faq) {
        await dispatch(updateFaq({ faqId: faq.id, data }));
        toast.success('FAQ updated successfully');
      } else {
        await dispatch(createFaq(data));
        toast.success('FAQ created successfully');
      }
      onSaved?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save FAQ');
    }
  };

  const categoryOptions = useMemo(() => (
    categoryData && categoryData.map((item) => ({ value: item.id, label: item.name }))
  ), [categoryData]);

  const footer = (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
      <Button variant="ghost" onClick={closeModal} size="sm">
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit(onSubmit)} size="sm">
        {faq ? 'Update FAQ' : 'Create FAQ'}
      </Button>
    </div>
  );

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={title}
      width="550px"
      footer={footer}
    >
      <form className="tw-space-y-5">
        {/* Info Alert */}
        <div className="tw-flex tw-items-start tw-gap-3 tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded">
          <FiAlertCircle className="tw-text-blue-600 tw-flex-shrink-0 tw-mt-0.5" size={18} />
          <p className="tw-text-sm tw-text-blue-800">
            Create clear and concise FAQs to help users understand your portal better.
          </p>
        </div>

        {/* Title Field */}
        <FormInput
          label="FAQ Title"
          placeholder="e.g., How do I reset my password?"
          required
          error={errors.title?.message}
          {...register("title", {
            required: "Title is required"
          })}
        />

        {/* Category Field */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">
            Category
          </label>
          <Controller
            control={control}
            name="category_id"
            rules={{ required: "Category is required" }}
            render={({ field }) => (
              <FormSelect
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                options={[
                  { value: '', label: 'Select a category...' },
                  ...(categoryOptions || [])
                ]}
                error={errors.category_id?.message}
              />
            )}
          />
        </div>

        {/* Description Field */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">
            Description
          </label>
          <div className="tw-rounded tw-overflow-hidden tw-border tw-border-slate-300">
            <Controller
              control={control}
              name="description"
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <TinymceEditor
                  height="300px"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
          {errors.description && (
            <p className="tw-text-sm tw-text-red-600 tw-mt-1.5">{errors.description.message}</p>
          )}
        </div>
      </form>
    </Drawer>
  );
};

FaqForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  faq: PropTypes.object,
  onSaved: PropTypes.func,
};

export default FaqForm;